import type { FilterObject } from '@moment/trading-types/filter-expression'
import type { FilterList } from '@/legacy-api/filter-lists-slice.gen'
import type { TargetRangeMap } from '@/portfolio/proposal-builder/components/interactive-bar-chart/hooks/useTargetRanges'
import type { ConstraintDisplayMode } from '@/portfolio/proposal-builder/components/interactive-bar-chart/types'
import type { RiskFactorBarChartState } from '@/portfolio/proposal-builder/components/risk-factor-bar-chart/types'
import type { BarClickData } from '@/portfolio/proposal-builder/helpers/create-bond-table'
import type {
  FilterListConstraints,
  RiskFactor,
} from '@/portfolio/proposal-builder/types'
import type {
  AllocationConstraint,
  UniverseConstraint,
} from '@/types/api/constraints'

import {
  AllocationConstraintType,
  UniverseConstraintType,
} from '@moment/horizon-data-access/proposal'
import { Operator } from '@moment/trading-types/filter'
import {
  isAndCondition,
  isOrCondition,
} from '@moment/trading-types/filter-expression'
import isEqual from 'lodash/isEqual'

import {
  extractScope,
  hasScope,
} from '@/portfolio/proposal-builder/data/helpers/constraint-scope'
import { generateId } from '@/portfolio/proposal-builder/data/helpers/sleeve-manager'
import { renderAllocationRange } from '@/portfolio/shared/constraint/utils'
import { capitalize } from '@/utils/presentation/formatting'

export type FilterListsMap = Record<string, FilterList>

export type FilterListConstraintType = 'inclusion' | 'exclusion' | 'allocation'

/**
 * The scope every filter-list rule shares, or undefined when they differ — each
 * rule carries its own, so one rule's scope must not be labelled as the card's.
 */
export function sharedFilterListScope(
  constraints: FilterListConstraints | undefined,
): FilterObject | undefined {
  const rules = [
    constraints?.inclusionConstraint,
    constraints?.exclusionConstraint,
    ...(constraints?.allocationConstraints ?? []),
  ].filter((rule) => rule !== undefined)

  const slices = rules.map((rule) => {
    const scope = extractScope(rule)
    return hasScope(scope) ? scope : undefined
  })

  if (slices.length === 0) return undefined
  const [first] = slices
  return slices.every((slice) => isEqual(slice, first)) ? first : undefined
}

/**
 * Render human-readable text for a filter list constraint.
 * @param constraint - The constraint to render
 * @param filterListsMap - Map of list IDs to filter list objects
 * @param type - The type of filter list constraint
 * @param displayMode - Display mode (percentage or dollar)
 * @param displayScaleFactor - Total investment amount for dollar mode
 */
export function renderFilterListConstraintText(
  constraint: UniverseConstraint | AllocationConstraint,
  filterListsMap: FilterListsMap,
  type: FilterListConstraintType,
  displayMode: ConstraintDisplayMode = 'percentage',
  displayScaleFactor?: number,
): string {
  let listIds: string[]

  if (type === 'inclusion') {
    listIds = extractIncludedFilterListIdsFromUniverseConstraint(
      constraint as UniverseConstraint,
    )
  } else if (type === 'exclusion') {
    listIds = extractExcludedFilterListIdsFromUniverseConstraint(
      constraint as UniverseConstraint,
    )
  } else {
    listIds = extractFilterListIdsFromAllocationConstraint(
      constraint as AllocationConstraint,
    )
  }

  const names = listIds.map((id) => filterListsMap[id]?.name ?? id).join(', ')

  if (type === 'inclusion') {
    return capitalize(`include bonds in ${names}`)
  }

  if (type === 'exclusion') {
    return capitalize(`exclude bonds in ${names}`)
  }

  // Allocation constraint
  const allocConstraint = constraint as AllocationConstraint
  const rangeDescription = renderAllocationRange(
    allocConstraint,
    displayMode,
    displayScaleFactor,
  )

  if (rangeDescription && names) {
    return capitalize(`allocate ${rangeDescription} to ${names}`)
  }
  return capitalize(`${rangeDescription} allocation to ${names}`)
}

/**
 * Read a string filter-list id for `operator`, or `undefined` if the condition
 * isn't a non-null object or the operator value isn't a string.
 */
function readFilterListId(
  condition: unknown,
  operator: Operator,
): string | undefined {
  if (typeof condition !== 'object' || condition === null) return undefined
  if (!(operator in condition)) return undefined
  const value = (condition as Record<string, unknown>)[operator]
  return typeof value === 'string' ? value : undefined
}

/**
 * True when every leaf condition uses only filter-list operators — i.e. the
 * whole filter is representable by the FilterLists card. Mixed filters (a
 * filter-list condition alongside a regular one) must NOT be claimed into a
 * filter-list slot: the card's editors rebuild the filter from list ids only
 * and would destroy the regular clause.
 */
export function isPureFilterListFilter(filter: FilterObject): boolean {
  const entries = Object.entries(filter)
  if (entries.length === 0) return false
  return entries.every(([key, value]) => {
    if (key === 'or' || key === 'and') {
      return (
        Array.isArray(value) &&
        value.every((branch) => isPureFilterListFilter(branch as FilterObject))
      )
    }
    if (key === 'not') return false
    if (typeof value !== 'object' || value === null || Array.isArray(value)) {
      return false
    }
    const operators = Object.keys(value)
    return (
      operators.length > 0 &&
      operators.every(
        (operator) =>
          operator === Operator.InFilterList ||
          operator === Operator.NotInFilterList,
      )
    )
  })
}

/** Extract filter list IDs from in_filter_list conditions (handles simple and OR). */
export function extractIncludedFilterListIdsFromUniverseConstraint(
  constraint: UniverseConstraint,
): string[] {
  const listIds: string[] = []
  const filter = constraint.filter

  // Recurse into OR conditions
  if (isOrCondition(filter)) {
    for (const condition of filter.or) {
      for (const [_field, fieldCondition] of Object.entries(condition)) {
        const listId = readFilterListId(fieldCondition, Operator.InFilterList)
        if (listId !== undefined) listIds.push(listId)
      }
    }
  }

  // Always check direct field conditions (excluding structural operators)
  for (const [key, condition] of Object.entries(filter)) {
    if (key === 'or' || key === 'and' || key === 'not') continue
    const listId = readFilterListId(condition, Operator.InFilterList)
    if (listId !== undefined) listIds.push(listId)
  }

  return listIds
}

/** Extract filter list IDs from not_in_filter_list conditions (handles simple and AND). */
export function extractExcludedFilterListIdsFromUniverseConstraint(
  constraint: UniverseConstraint,
): string[] {
  const filter = constraint.filter
  const listIds: string[] = []

  // Recurse into AND conditions
  if (isAndCondition(filter)) {
    for (const condition of filter.and) {
      for (const [_field, fieldCondition] of Object.entries(condition)) {
        const listId = readFilterListId(
          fieldCondition,
          Operator.NotInFilterList,
        )
        if (listId !== undefined) listIds.push(listId)
      }
    }
  }

  // Always check direct field conditions (excluding structural operators)
  for (const [key, condition] of Object.entries(filter)) {
    if (key === 'or' || key === 'and' || key === 'not') continue
    const listId = readFilterListId(condition, Operator.NotInFilterList)
    if (listId !== undefined) listIds.push(listId)
  }

  return listIds
}

export function extractFilterListIdsFromAllocationConstraint(
  constraint: AllocationConstraint,
): string[] {
  const listIds: string[] = []
  const filter = constraint.filter
  for (const [key, condition] of Object.entries(filter)) {
    if (key === 'or' || key === 'and' || key === 'not') continue
    const listId = readFilterListId(condition, Operator.InFilterList)
    if (listId !== undefined) listIds.push(listId)
  }

  return listIds
}

/** Initialize UI state from a FilterLists RiskFactor. Categories are keyed by list_id. */
export function initializeFilterListStateFromRiskFactor(
  riskFactor: RiskFactor | undefined,
): RiskFactorBarChartState {
  const targetRanges: TargetRangeMap = new Map()
  const excludedCategories = new Set<string>()
  const includedCategories = new Set<string>()

  if (!riskFactor) {
    return {
      maxWeight: undefined,
      targetRanges,
      excludedCategories,
      includedCategories,
    }
  }

  // Get FilterListConstraints from the new property
  if (!riskFactor.filterListConstraints) {
    return {
      maxWeight: undefined,
      targetRanges,
      excludedCategories,
      includedCategories,
    }
  }
  const constraints = riskFactor.filterListConstraints

  // Process inclusion constraint (OR of filter lists)
  // Use listId as the category key for uniqueness
  if (constraints.inclusionConstraint?.filter) {
    const includedListIds = extractIncludedFilterListIdsFromUniverseConstraint(
      constraints.inclusionConstraint,
    )
    for (const listId of includedListIds) {
      includedCategories.add(listId)
    }
  }

  // Process exclusion constraint (AND of filter lists)
  // Use listId as the category key for uniqueness
  if (constraints.exclusionConstraint?.filter) {
    const excludedListIds = extractExcludedFilterListIdsFromUniverseConstraint(
      constraints.exclusionConstraint,
    )
    for (const listId of excludedListIds) {
      excludedCategories.add(listId)
    }
  }

  // Process allocation constraints
  // Use listId as the category key for uniqueness
  for (const allocationConstraint of constraints.allocationConstraints) {
    const listIds =
      extractFilterListIdsFromAllocationConstraint(allocationConstraint)
    for (const listId of listIds) {
      const minWeight = allocationConstraint.min_weight ?? 0
      const maxWeight = allocationConstraint.max_weight ?? 1
      targetRanges.set(listId, {
        min: minWeight * 100,
        max: maxWeight * 100,
      })
    }
  }

  return {
    maxWeight: undefined, // Not applicable for filter lists
    targetRanges,
    excludedCategories,
    includedCategories,
  }
}

/**
 * Generate FilterListConstraints from UI state.
 * Inclusions combine into OR, exclusions combine into AND, allocations are per-list.
 *
 * When `existingConstraints` is provided, the inclusion/exclusion aggregate ids
 * and per-list allocation ids (and BE-attached fields) are inherited rather than re-minted.
 * See "Constraint ID Preservation" in the proposal-builder CLAUDE.md.
 */
export function createFilterListConstraintsFromState({
  state,
  filterListsMap,
  existingConstraints,
}: {
  state: RiskFactorBarChartState
  filterListsMap: FilterListsMap
  existingConstraints?: FilterListConstraints
}): FilterListConstraints {
  const constraints: FilterListConstraints = {
    inclusionConstraint: undefined,
    exclusionConstraint: undefined,
    allocationConstraints: [],
  }

  // Process included filter lists - combine into a single OR constraint
  const includedFilterLists: FilterList[] = []
  for (const listId of state.includedCategories) {
    const filterList = filterListsMap[listId]
    if (filterList) {
      includedFilterLists.push(filterList)
    }
  }

  if (includedFilterLists.length > 0) {
    const existing = existingConstraints?.inclusionConstraint
    constraints.inclusionConstraint = {
      ...existing,
      // Reuse the aggregate's id while >=1 list remains; fresh id only on empty->populated
      id: existing?.id ?? generateId(),
      type: UniverseConstraintType.universe,
      filter: {
        or: includedFilterLists.map((filterList) => ({
          [filterList.field.toLowerCase()]: {
            [Operator.InFilterList]: filterList.list_id,
          },
        })),
      },
    }
  }

  // Process excluded filter lists - combine into a single AND constraint
  const excludedFilterLists: FilterList[] = []
  for (const listId of state.excludedCategories) {
    const filterList = filterListsMap[listId]
    if (filterList) {
      excludedFilterLists.push(filterList)
    }
  }

  if (excludedFilterLists.length > 0) {
    const existing = existingConstraints?.exclusionConstraint
    constraints.exclusionConstraint = {
      ...existing,
      // Reuse the aggregate's id while >=1 list remains; fresh id only on empty->populated
      id: existing?.id ?? generateId(),
      type: UniverseConstraintType.universe,
      filter: {
        and: excludedFilterLists.map((filterList) => ({
          [filterList.field.toLowerCase()]: {
            [Operator.NotInFilterList]: filterList.list_id,
          },
        })),
      },
    }
  }

  // Per-list allocation constraints from the loaded state, keyed by list_id, so
  // an edited list's allocation keeps its id while a new list mints a fresh one.
  const existingAllocationsByListId = new Map<string, AllocationConstraint>()
  for (const allocationConstraint of existingConstraints?.allocationConstraints ??
    []) {
    const listIds =
      extractFilterListIdsFromAllocationConstraint(allocationConstraint)
    for (const listId of listIds) {
      existingAllocationsByListId.set(listId, allocationConstraint)
    }
  }

  // Process target ranges - each gets its own allocation constraint
  for (const [listId, allocation] of state.targetRanges) {
    const filterList = filterListsMap[listId]
    if (!filterList) continue

    if (allocation.min !== undefined || allocation.max !== undefined) {
      const existing = existingAllocationsByListId.get(filterList.list_id)
      constraints.allocationConstraints.push({
        ...existing,
        // Reuse the matching list's allocation id so identity is stable across edits
        id: existing?.id ?? generateId(),
        type: AllocationConstraintType.allocation,
        filter: {
          [filterList.field.toLowerCase()]: {
            [Operator.InFilterList]: filterList.list_id,
          },
        },
        min_weight:
          allocation.min !== undefined ? allocation.min / 100 : undefined,
        max_weight:
          allocation.max !== undefined ? allocation.max / 100 : undefined,
      })
    }
  }

  return constraints
}

/** Create filter object when user clicks on a bar */
export function createFilterListFilters(
  barData: BarClickData,
  filterListsMap: FilterListsMap,
): FilterObject {
  const { category: listId } = barData
  if (!listId) {
    return {}
  }

  const filterList = filterListsMap[listId]

  if (!filterList) {
    return {}
  }

  return {
    [filterList.field.toLowerCase()]: {
      [Operator.InFilterList]: filterList.list_id,
    },
  }
}
